import { uid as defaultUid, type Uid } from '../model/ids.ts';
import { DEFAULT_LINE_COLOR, labelFor, labelForExact, normalizeHex } from '../model/lineColors.ts';
import { clampFrameWidthFt, DEFAULT_FRAME_FT } from '../model/scale.ts';
import type { LegendLabel, LightStyle, Photo, Run, TimeOfDay, TimePreset, Vec2 } from '../model/types.ts';
import type { ProjectRecord } from './library.ts';
import { DEFAULT_STYLE } from './settings.ts';

/**
 * The open project + editor state and its undo/redo, as a pure reducer.
 *
 * The action bodies are copied from the original zustand store (`state/project.ts`) with
 * minimal edits; they run against a tiny synchronous `set`/`get` shim with zustand's
 * semantics (shallow merge; a setter returning the same state object is a no-op). I/O is
 * injected / returned instead of performed:
 * - `env.uid` replaces the module-level `uid()` (deterministic in tests and fixtures);
 * - `env.settings` replaces `useSettings.getState()` (read-only here);
 * - writing a remembered legend label and deleting files come back as `effects`.
 *
 * Behavior: by default the FIXED rules every new app ships (env.behavior = 'fixed'):
 * - cancelling a 1-point run also removes the history entry its addPoint created, so there is
 *   no empty undo step (known issue 07 §B4);
 * - line colors and legend keys are normalized to uppercase #RRGGBB (setDrawLineColor,
 *   updateRun, setLegendLabel) and legend labels are looked up case-insensitively (§B9).
 * env.behavior = 'original' reproduces the original app exactly (cross-check, fixtures).
 */

/** draw: tap corners · freehand: trace the roofline, snapped onto it · select: edit runs. */
export type Tool = 'draw' | 'freehand' | 'select';
export type ViewMode = 'demo' | 'lines';

export interface AiResult {
  id: string;
  time: TimePreset;
  /** data: URI (web) or file:// URI (native) of the generated JPEG. */
  uri: string;
  width: number;
  height: number;
  createdAt: number;
  prompt: string;
}

/** A full-resolution image the user exported, kept so it can be found again from the gallery. */
export interface ExportRecord {
  id: string;
  kind: 'demo' | 'lines';
  /** data: URI (web) or file:// URI (native) of the exported JPEG. */
  uri: string;
  width: number;
  height: number;
  createdAt: number;
}

export const HISTORY_LIMIT = 100;

/** Measured length per line color, feet > 0, keyed by uppercase #RRGGBB (docs/spec/09 §1). */
export type MeasuredFt = Record<string, number>;

/**
 * One undo/redo entry. The original history holds whole `runs` snapshots only (arrays); a
 * measured-length edit stores the previous measuredFt instead (null = absent), so it is one
 * undo step without touching runs.
 */
export type HistoryEntry = Run[] | { measuredFt: MeasuredFt | null };

const isRunsEntry = (e: HistoryEntry): e is Run[] => Array.isArray(e);
export { DEFAULT_FRAME_FT };
export const PROJECT_KEY = 'better-lights.project.v1';
export const PROJECT_VERSION = 3;

/** Data of the project store (the original `ProjectState` without its action functions). */
export interface ProjectState {
  /** Gallery id of the open project; null when no photo is open. */
  projectId: string | null;
  photo: Photo | null;
  runs: Run[];
  past: HistoryEntry[];
  future: HistoryEntry[];
  /** Run receiving taps in draw mode. */
  activeRunId: string | null;
  selectedRunId: string | null;
  tool: Tool;
  view: ViewMode;
  time: TimeOfDay;
  colorId: string;
  style: LightStyle;
  /** Feet of house the photo spans; null = estimated from the drawn runs (see estimateFrameFt). */
  frameWidthFt: number | null;
  /** Line color given to newly drawn runs. */
  drawLineColor: string;
  /** This project's legend: what each line color means (saved with the project). */
  legendLabels: Record<string, LegendLabel>;
  aiResults: AiResult[];
  exports: ExportRecord[];
  /** Measured lengths per line color (new in the rewrite); absent until one is set. */
  measuredFt?: MeasuredFt;
}

interface ProjectActions {
  /** Starts a new project (a new gallery entry) from a photo. */
  openPhoto: (photo: Photo, defaults: { style: LightStyle; colorId: string }) => void;
  /** Opens a project saved in the gallery. */
  loadProject: (record: ProjectRecord) => void;
  closeProject: () => void;

  addPoint: (p: Vec2) => void;
  /** Adds a finished run in one step (freehand strokes). */
  addRun: (points: Vec2[]) => void;
  finishRun: () => void;
  /** Call once before a drag so the whole drag is one undo step. */
  checkpoint: () => void;
  movePoint: (runId: string, index: number, p: Vec2) => void;
  insertPoint: (runId: string, index: number, p: Vec2) => void;
  removePoint: (runId: string, index: number) => void;
  deleteRun: (runId: string) => void;
  clearRuns: () => void;
  selectRun: (runId: string | null) => void;
  updateRun: (runId: string, patch: Partial<Pick<Run, 'lineColor'>>) => void;
  undo: () => void;
  redo: () => void;

  setTool: (tool: Tool) => void;
  setView: (view: ViewMode) => void;
  setTime: (time: TimeOfDay) => void;
  /** Light look for the whole house (color preset or theme). */
  setColor: (colorId: string) => void;
  setStyle: (patch: Partial<LightStyle>) => void;
  setFrameWidthFt: (ft: number | null) => void;
  setDrawLineColor: (hex: string) => void;
  /** Edits this project's legend; also updates the price list when "Remember" is on. */
  setLegendLabel: (hex: string, patch: Partial<LegendLabel>) => void;

  addAiResults: (results: AiResult[]) => void;
  /** Removes a render from the project and deletes its file. */
  removeAiResult: (id: string) => void;
  addExport: (record: ExportRecord) => void;
  /** Removes an export from the project and deletes its file. */
  removeExport: (id: string) => void;
  /** Sets (feet > 0) or clears (null) the measured length of a line color; one undo step. */
  setMeasuredLength: (hex: string, feet: number | null) => void;
}

/** The saved part of a project, apart from its photo and files. */
export type ProjectDoc = Pick<
  ProjectState,
  'runs' | 'time' | 'colorId' | 'style' | 'frameWidthFt' | 'drawLineColor' | 'legendLabels' | 'view' | 'measuredFt'
>;

export function projectDocOf(s: ProjectDoc): ProjectDoc {
  return {
    runs: s.runs,
    time: s.time,
    colorId: s.colorId,
    style: s.style,
    frameWidthFt: s.frameWidthFt,
    drawLineColor: s.drawLineColor,
    legendLabels: s.legendLabels,
    view: s.view,
    ...(s.measuredFt ? { measuredFt: s.measuredFt } : {}),
  };
}

const initial: ProjectState = {
  projectId: null as string | null,
  photo: null,
  runs: [] as Run[],
  past: [] as HistoryEntry[],
  future: [] as HistoryEntry[],
  activeRunId: null,
  selectedRunId: null,
  tool: 'draw' as Tool,
  // Projects open in Lines & pricing: draw first, then flip to the lighting demo.
  view: 'lines' as ViewMode,
  time: 1,
  colorId: 'warm-2700',
  style: DEFAULT_STYLE,
  frameWidthFt: null as number | null,
  drawLineColor: DEFAULT_LINE_COLOR,
  legendLabels: {} as Record<string, LegendLabel>,
  aiResults: [] as AiResult[],
  exports: [] as ExportRecord[],
  // Present (as undefined) so `{...initial}` resets it; dropped from JSON.
  measuredFt: undefined,
};

/** A fresh, closed project store (the original's initial state). */
export function initialProjectState(): ProjectState {
  return { ...initial };
}

/* ------------------------------------------------------------------ environment */

export interface ProjectEnv {
  uid: Uid;
  /** 'fixed' (default) or 'original' (reproduces the original app's known bugs). */
  behavior?: 'fixed' | 'original';
  /** Snapshot of the settings the store reads. */
  settings: { legendLabels: Record<string, LegendLabel>; rememberLegend: boolean };
}

export type ProjectEffect =
  /** Write the merged label to the settings price list (`settings.setLegendLabel(hex, label)`). */
  | { type: 'rememberLegendLabel'; hex: string; label: LegendLabel }
  /** Delete an app file, best effort (`deleteAppFile(uri)`). */
  | { type: 'deleteFile'; uri: string };

const defaultEnv: ProjectEnv = { uid: defaultUid, settings: { legendLabels: {}, rememberLegend: false } };

/* ---------------------------------------------------------------------- actions */

export type ProjectAction =
  | { type: 'openPhoto'; photo: Photo; defaults: { style: LightStyle; colorId: string } }
  | { type: 'loadProject'; record: ProjectRecord }
  | { type: 'closeProject' }
  | { type: 'addPoint'; p: Vec2 }
  | { type: 'addRun'; points: Vec2[] }
  | { type: 'finishRun' }
  | { type: 'checkpoint' }
  | { type: 'movePoint'; runId: string; index: number; p: Vec2 }
  | { type: 'insertPoint'; runId: string; index: number; p: Vec2 }
  | { type: 'removePoint'; runId: string; index: number }
  | { type: 'deleteRun'; runId: string }
  | { type: 'clearRuns' }
  | { type: 'selectRun'; runId: string | null }
  | { type: 'updateRun'; runId: string; patch: Partial<Pick<Run, 'lineColor'>> }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'setTool'; tool: Tool }
  | { type: 'setView'; view: ViewMode }
  | { type: 'setTime'; time: TimeOfDay }
  | { type: 'setColor'; colorId: string }
  | { type: 'setStyle'; patch: Partial<LightStyle> }
  | { type: 'setFrameWidthFt'; ft: number | null }
  | { type: 'setDrawLineColor'; hex: string }
  | { type: 'setLegendLabel'; hex: string; patch: Partial<LegendLabel> }
  | { type: 'addAiResults'; results: AiResult[] }
  | { type: 'removeAiResult'; id: string }
  | { type: 'addExport'; record: ExportRecord }
  | { type: 'removeExport'; id: string }
  | { type: 'setMeasuredLength'; hex: string; feet: number | null };

type SetState = (partial: Partial<ProjectState> | ((s: ProjectState) => Partial<ProjectState> | ProjectState)) => void;
type GetState = () => ProjectState & ProjectActions;

function createActions(set: SetState, get: GetState, env: ProjectEnv, emit: (e: ProjectEffect) => void): ProjectActions {
  const uid = env.uid;
  const fixed = env.behavior !== 'original';
  const hexOf = fixed ? normalizeHex : (hex: string) => hex;
  const labelOf = fixed ? labelFor : labelForExact;
  const deleteAppFile = (uri: string) => emit({ type: 'deleteFile', uri });

  /** Applies a change to `runs` as one undoable step. */
  const edit = (fn: (runs: Run[]) => Run[], extra: Partial<ProjectState> = {}) =>
    set((s) => ({
      past: [...s.past, s.runs].slice(-HISTORY_LIMIT),
      future: [],
      runs: fn(s.runs),
      ...extra,
    }));

  const mapRun = (runs: Run[], id: string, fn: (r: Run) => Run) => runs.map((r) => (r.id === id ? fn(r) : r));

  return {
    openPhoto: (photo, defaults) =>
      set({
        ...initial,
        projectId: uid('proj'),
        photo,
        style: defaults.style,
        colorId: defaults.colorId,
        drawLineColor: get().drawLineColor,
        // New projects start from the saved price list.
        legendLabels: { ...env.settings.legendLabels },
      }),
    loadProject: (record) =>
      set({
        ...initial,
        ...record.doc,
        // Always reopen in Lines & pricing; the saved view only round-trips in the document.
        view: initial.view,
        projectId: record.id,
        photo: record.photo,
        aiResults: record.aiResults,
        exports: record.exports,
      }),
    closeProject: () => set({ ...initial }),

    addPoint: (p) => {
      const { activeRunId, drawLineColor } = get();
      if (activeRunId) {
        edit((runs) => mapRun(runs, activeRunId, (r) => ({ ...r, points: [...r.points, p] })));
        return;
      }
      const run: Run = { id: uid('run'), points: [p], lineColor: drawLineColor };
      edit((runs) => [...runs, run], { activeRunId: run.id, selectedRunId: run.id });
    },

    addRun: (points) => {
      if (points.length < 2) return;
      get().finishRun();
      const run: Run = { id: uid('run'), points, lineColor: get().drawLineColor };
      edit((runs) => [...runs, run]);
    },

    finishRun: () => {
      const { activeRunId, runs } = get();
      if (!activeRunId) return;
      const run = runs.find((r) => r.id === activeRunId);
      if (run && run.points.length < 2) {
        // A single tap isn't a run; drop it without adding another history step.
        set({ runs: runs.filter((r) => r.id !== activeRunId), activeRunId: null, selectedRunId: null });
        // Fixed (07 §B4): also drop the history entry the tap's addPoint created, when it is
        // exactly the state we are back to, so undo doesn't need an extra empty step.
        if (fixed) {
          set((s) => {
            const last = s.past[s.past.length - 1];
            return last && isRunsEntry(last) && sameRuns(last, s.runs) ? { past: s.past.slice(0, -1) } : s;
          });
        }
        return;
      }
      set({ activeRunId: null, selectedRunId: null });
    },

    checkpoint: () => set((s) => ({ past: [...s.past, s.runs].slice(-HISTORY_LIMIT), future: [] })),

    movePoint: (runId, index, p) =>
      set((s) => ({
        runs: mapRun(s.runs, runId, (r) => ({ ...r, points: r.points.map((q, i) => (i === index ? p : q)) })),
      })),

    insertPoint: (runId, index, p) =>
      edit((runs) =>
        mapRun(runs, runId, (r) => ({ ...r, points: [...r.points.slice(0, index), p, ...r.points.slice(index)] })),
      ),

    removePoint: (runId, index) => {
      const run = get().runs.find((r) => r.id === runId);
      if (!run) return;
      if (run.points.length <= 2) {
        get().deleteRun(runId);
        return;
      }
      edit((runs) => mapRun(runs, runId, (r) => ({ ...r, points: r.points.filter((_, i) => i !== index) })));
    },

    deleteRun: (runId) =>
      edit((runs) => runs.filter((r) => r.id !== runId), {
        selectedRunId: get().selectedRunId === runId ? null : get().selectedRunId,
        activeRunId: get().activeRunId === runId ? null : get().activeRunId,
      }),

    clearRuns: () => edit(() => [], { selectedRunId: null, activeRunId: null }),

    selectRun: (selectedRunId) => {
      if (get().activeRunId && selectedRunId !== get().activeRunId) get().finishRun();
      set({ selectedRunId });
    },

    updateRun: (runId, patch) => {
      const p = fixed && patch.lineColor !== undefined ? { ...patch, lineColor: hexOf(patch.lineColor) } : patch;
      edit((runs) => mapRun(runs, runId, (r) => ({ ...r, ...p })));
    },

    undo: () =>
      set((s) => {
        if (s.past.length === 0) return s;
        const entry = s.past[s.past.length - 1];
        if (!isRunsEntry(entry)) {
          return { measuredFt: entry.measuredFt ?? undefined, past: s.past.slice(0, -1), future: [{ measuredFt: s.measuredFt ?? null }, ...s.future] };
        }
        const runs = entry;
        return {
          runs,
          past: s.past.slice(0, -1),
          future: [s.runs, ...s.future],
          ...keepValidRefs(s, runs),
        };
      }),

    redo: () =>
      set((s) => {
        if (s.future.length === 0) return s;
        const entry = s.future[0];
        if (!isRunsEntry(entry)) {
          return { measuredFt: entry.measuredFt ?? undefined, past: [...s.past, { measuredFt: s.measuredFt ?? null }], future: s.future.slice(1) };
        }
        const runs = entry;
        return {
          runs,
          past: [...s.past, s.runs],
          future: s.future.slice(1),
          ...keepValidRefs(s, runs),
        };
      }),

    setTool: (tool) => {
      if (tool !== 'draw') get().finishRun();
      set({ tool });
    },
    setView: (view) => set({ view }),
    setTime: (time) => set({ time: Math.max(0, Math.min(1, time)) }),
    setColor: (colorId) => set({ colorId }),
    setStyle: (patch) => set((s) => ({ style: { ...s.style, ...patch } })),
    setFrameWidthFt: (ft) => set({ frameWidthFt: clampFrameWidthFt(ft) }),
    setLegendLabel: (rawHex, patch) => {
      const hex = hexOf(rawHex);
      set((s) => ({ legendLabels: { ...s.legendLabels, [hex]: { ...labelOf(s.legendLabels, hex), ...patch } } }));
      if (env.settings.rememberLegend) emit({ type: 'rememberLegendLabel', hex, label: get().legendLabels[hex] });
    },
    setDrawLineColor: (rawHex) => {
      const drawLineColor = hexOf(rawHex);
      // Picking a color mid-run recolors the run being drawn, too.
      const { activeRunId } = get();
      set((s) => ({ drawLineColor, runs: activeRunId ? mapRun(s.runs, activeRunId, (r) => ({ ...r, lineColor: drawLineColor })) : s.runs }));
    },

    addAiResults: (results) => set((s) => ({ aiResults: [...results, ...s.aiResults] })),
    removeAiResult: (id) => {
      const result = get().aiResults.find((r) => r.id === id);
      if (result) void deleteAppFile(result.uri);
      set((s) => ({ aiResults: s.aiResults.filter((r) => r.id !== id) }));
    },
    addExport: (record) => set((s) => ({ exports: [record, ...s.exports] })),
    setMeasuredLength: (rawHex, feet) => {
      // New in the rewrite: hex always normalized; invalid lengths are ignored; no-ops add no step.
      const hex = normalizeHex(rawHex);
      if (feet !== null && !(Number.isFinite(feet) && feet > 0)) return;
      const cur = get().measuredFt;
      if ((cur?.[hex] ?? null) === feet) return;
      const next: MeasuredFt = { ...cur };
      if (feet === null) delete next[hex];
      else next[hex] = feet;
      set((s) => ({
        past: [...s.past, { measuredFt: s.measuredFt ?? null }].slice(-HISTORY_LIMIT),
        future: [],
        measuredFt: Object.keys(next).length ? next : undefined,
      }));
    },
    removeExport: (id) => {
      const record = get().exports.find((r) => r.id === id);
      if (record) void deleteAppFile(record.uri);
      set((s) => ({ exports: s.exports.filter((r) => r.id !== id) }));
    },
  };
}

/** Structural equality of two runs arrays (ids, colors, points). */
function sameRuns(a: Run[], b: Run[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x === y) continue;
    if (x.id !== y.id || x.lineColor !== y.lineColor || x.points.length !== y.points.length) return false;
    for (let j = 0; j < x.points.length; j++) {
      if (x.points[j].x !== y.points[j].x || x.points[j].y !== y.points[j].y) return false;
    }
  }
  return true;
}

function keepValidRefs(s: ProjectState, runs: Run[]): Partial<ProjectState> {
  const ids = new Set(runs.map((r) => r.id));
  return {
    activeRunId: s.activeRunId && ids.has(s.activeRunId) ? s.activeRunId : null,
    selectedRunId: s.selectedRunId && ids.has(s.selectedRunId) ? s.selectedRunId : null,
  };
}

/* ---------------------------------------------------------------------- reducer */

export interface ReduceResult {
  state: ProjectState;
  effects: ProjectEffect[];
}

/**
 * Applies one action. Never mutates `state`; unchanged parts are shared by reference
 * (like zustand), so `next.runs === prev.runs` means "runs did not change".
 */
export function reduceProject(state: ProjectState, action: ProjectAction, env: ProjectEnv = defaultEnv): ReduceResult {
  let cur = state;
  const effects: ProjectEffect[] = [];
  const set: SetState = (partial) => {
    const next = typeof partial === 'function' ? partial(cur) : partial;
    if (!Object.is(next, cur)) cur = Object.assign({}, cur, next);
  };
  const get: GetState = () => ({ ...cur, ...actions });
  const actions = createActions(set, get, env, (e) => effects.push(e));

  switch (action.type) {
    case 'openPhoto': actions.openPhoto(action.photo, action.defaults); break;
    case 'loadProject': actions.loadProject(action.record); break;
    case 'closeProject': actions.closeProject(); break;
    case 'addPoint': actions.addPoint(action.p); break;
    case 'addRun': actions.addRun(action.points); break;
    case 'finishRun': actions.finishRun(); break;
    case 'checkpoint': actions.checkpoint(); break;
    case 'movePoint': actions.movePoint(action.runId, action.index, action.p); break;
    case 'insertPoint': actions.insertPoint(action.runId, action.index, action.p); break;
    case 'removePoint': actions.removePoint(action.runId, action.index); break;
    case 'deleteRun': actions.deleteRun(action.runId); break;
    case 'clearRuns': actions.clearRuns(); break;
    case 'selectRun': actions.selectRun(action.runId); break;
    case 'updateRun': actions.updateRun(action.runId, action.patch); break;
    case 'undo': actions.undo(); break;
    case 'redo': actions.redo(); break;
    case 'setTool': actions.setTool(action.tool); break;
    case 'setView': actions.setView(action.view); break;
    case 'setTime': actions.setTime(action.time); break;
    case 'setColor': actions.setColor(action.colorId); break;
    case 'setStyle': actions.setStyle(action.patch); break;
    case 'setFrameWidthFt': actions.setFrameWidthFt(action.ft); break;
    case 'setDrawLineColor': actions.setDrawLineColor(action.hex); break;
    case 'setLegendLabel': actions.setLegendLabel(action.hex, action.patch); break;
    case 'addAiResults': actions.addAiResults(action.results); break;
    case 'removeAiResult': actions.removeAiResult(action.id); break;
    case 'addExport': actions.addExport(action.record); break;
    case 'removeExport': actions.removeExport(action.id); break;
    case 'setMeasuredLength': actions.setMeasuredLength(action.hex, action.feet); break;
    default: {
      const never: never = action;
      throw new Error(`Unknown project action ${(never as { type: string }).type}`);
    }
  }
  return { state: cur, effects };
}

/** The persisted part of the open project (`partialize` of `better-lights.project.v1`). */
export function persistedProjectOf(s: ProjectState): Pick<ProjectState, 'projectId' | 'photo'> & ProjectDoc {
  return { projectId: s.projectId, photo: s.photo, ...projectDocOf(s) };
}

export const selectSelectedRun = (s: ProjectState) => s.runs.find((r) => r.id === s.selectedRunId) ?? null;

/* ------------------------------------------------------------------- migrations */

/**
 * Persist migration of `better-lights.project.v1` to version 3. Pure version of the original
 * async `migrate`: the storage reads are resolved by the caller (see `legacyTierColors` and
 * `savedPriceList` in settings.ts). Input is not mutated.
 */
export function migrateProject(
  persisted: unknown,
  version: number,
  sources: { tierColors: () => Map<string, string>; priceList: () => Record<string, LegendLabel> },
): Record<string, unknown> {
  const old = { ...((persisted ?? {}) as Record<string, any>) };
  if (version < 2) {
    // v1 runs carried a pricing tier id plus per-run light overrides; v2 runs carry a
    // line color and the light look is house-wide.
    const colors = sources.tierColors();
    old.runs = ((old.runs ?? []) as any[]).map((r) => ({
      id: r.id,
      points: r.points,
      lineColor: colors.get(r.tierId) ?? DEFAULT_LINE_COLOR,
    }));
    old.drawLineColor = colors.get(old.drawTierId) ?? DEFAULT_LINE_COLOR;
    delete old.drawTierId;
    if (old.frameWidthFt === DEFAULT_FRAME_FT) old.frameWidthFt = null;
  }
  if (version < 3 && !old.legendLabels) {
    // Projects started saving their own legend in v3; begin from the saved price list.
    old.legendLabels = sources.priceList();
  }
  return old;
}
